import React from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import { useStaticQuery, graphql } from "gatsby";
import { useLocation } from "@reach/router";

function SEO({
  description,
  lang,
  meta,
  title,
  structuredData,
  image,
  url,
  noindex,
}) {
  const { pathname } = useLocation();
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            author
            description
            title
            siteUrl
            keywords
          }
        }
      }
    `
  );

  const keywords = Array.isArray(site.siteMetadata.keywords) 
    ? site.siteMetadata.keywords.join(', ')
    : site.siteMetadata.keywords;
  const metaDescription = description || site.siteMetadata.description;
  const defaultTitle = site.siteMetadata?.title;
  const siteURL = site.siteMetadata.siteUrl;
  const metaImage = image || `${siteURL}/og-logo.png`;
  const metaUrl = url || `${siteURL}${pathname}`;

  // Dynamic title handling
  const maxTitleLength = 60;
  const baseBranding = "Reardon Web Development";
  const shortenedBranding = "RWD";

  const dynamicTitle =
    title.length + baseBranding.length + 3 > maxTitleLength
      ? title
      : `${title} | ${shortenedBranding}`;

  const truncatedTitle =
    dynamicTitle.length > maxTitleLength
      ? `${dynamicTitle.substring(0, maxTitleLength - 3)}...`
      : dynamicTitle;

  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "Reardon Web Development",
    image: `${siteURL}/og-logo.png`,
    address: {
      "@type": "PostalAddress",
      streetAddress: "108 West St",
      addressLocality: "Medway",
      addressRegion: "MA",
      postalCode: "02053",
      addressCountry: "US",
    },
    url: siteURL,
    telephone: "+1-(508)-254-6234",
    openingHours: "Mo-Fr 08:00-18:00",
    sameAs: [],
  };

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={truncatedTitle}
      meta={[
        {
          name: `description`,
          content: metaDescription,
        },
        {
          name: `keywords`,
          content: keywords,
        },
        {
          property: `og:title`,
          content: truncatedTitle,
        },
        {
          property: `og:description`,
          content: metaDescription,
        },
        {
          property: `og:type`,
          content: `website`,
        },
        {
          property: `og:image`,
          content: metaImage,
        },
        {
          property: `og:image:alt`,
          content: "Logo of Reardon Web Development",
        },
        {
          property: `og:image:width`,
          content: `1200`,
        },
        {
          property: `og:image:height`,
          content: `630`,
        },
        {
          property: `og:url`,
          content: metaUrl,
        },
        {
          property: `og:site_name`,
          content: defaultTitle,
        },
        {
          name: `twitter:card`,
          content: `summary_large_image`,
        },
        {
          name: `twitter:creator`,
          content: site.siteMetadata?.author || ``,
        },
        {
          name: `twitter:title`,
          content: truncatedTitle,
        },
        {
          name: `twitter:description`,
          content: metaDescription,
        },
        {
          property: `twitter:image`,
          content: metaImage,
        },
        // Conditionally add the noindex meta tag
        ...(noindex
          ? [
              {
                name: "robots",
                content: "noindex",
              },
            ]
          : []),
      ].concat(meta)}
    >
      <link rel="canonical" href={metaUrl} />
      <link rel="preconnect" href="https://www.googletagmanager.com" />
      <link rel="preconnect" href="https://www.google-analytics.com" />
      <link rel="preconnect" href="https://plausible.io" />
      <script type="application/ld+json">
        {JSON.stringify(structuredData || defaultStructuredData)}
      </script>

    </Helmet>
  );
}

SEO.defaultProps = {
  lang: `en`,
  meta: [],
  description: ``,
  structuredData: null,
  image: null,
  url: null,
  noindex: false,
};

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
  structuredData: PropTypes.object,
  image: PropTypes.string,
  url: PropTypes.string,
  noindex: PropTypes.bool,
};

export default SEO;
